
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LinkIcon, Loader2, ArrowRight } from 'lucide-react';
import { validateAndNormalizeUrl, debugUrlTransformation } from '@/utils/urlUtils';

interface FaqUrlInputProps {
  onSubmit: (url: string) => Promise<void>;
  isLoading: boolean;
}

const FaqUrlInput: React.FC<FaqUrlInputProps> = ({ onSubmit, isLoading }) => {
  const [url, setUrl] = useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (url.trim() && !isLoading) {
      debugUrlTransformation(url, 'FaqUrlInput.handleSubmit');
      
      const validation = validateAndNormalizeUrl(url);
      if (!validation.isValid) {
        console.error(`[FaqUrlInput] URL validation failed:`, validation);
        return;
      }
      
      console.log(`[FaqUrlInput] Submitting normalized URL: "${validation.normalizedUrl}"`);
      onSubmit(validation.normalizedUrl);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="w-full space-y-2">
      <div className="flex gap-2 items-center">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <LinkIcon className="h-4 w-4 text-purple-400" />
          </div>
          <Input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Enter URL to analyze (e.g., example.com/page)"
            className="pl-10 py-6 h-auto bg-gradient-to-r from-purple-500/5 to-indigo-500/5 focus:from-purple-500/10 focus:to-indigo-500/10 border-purple-500/20 focus-visible:ring-purple-500/30 transition-all duration-300"
            required
            disabled={isLoading}
          />
        </div>
        <Button 
          type="submit" 
          disabled={isLoading || !url.trim()}
          className="min-w-[120px] h-12 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white transition-all duration-300"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Analyzing
            </>
          ) : (
            <>
              Analyze URL
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground pl-1">
        Enter a full URL to generate FAQs from its content
      </p>
    </form>
  );
};

export default FaqUrlInput;
